'use client';

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import styles from './Pillars.module.css';

type PillarCardProps = {
  children: ReactNode;
};

const HOLD_MS = 200;
const MOVE_CANCEL_PX = 10;

export function PillarCard({ children }: PillarCardProps) {
  const [dismissed, setDismissed] = useState(false);
  const [holding, setHolding] = useState(false);
  const [latched, setLatched] = useState(false);
  const cardRef = useRef<HTMLLIElement>(null);
  const holdTimer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const activePointer = useRef<number | null>(null);
  const touched = useRef(false);
  const held = useRef(false);

  const clearTimer = () => {
    if (holdTimer.current != null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const endHold = () => {
    clearTimer();
    start.current = null;
    activePointer.current = null;
    setHolding(false);
  };

  useEffect(() => clearTimer, []);

  useEffect(() => {
    if (!latched) return undefined;
    const closeOutside = (event: globalThis.PointerEvent) => {
      if (!cardRef.current?.contains(event.target as Node)) setLatched(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [latched]);

  const isOnMedia = (target: EventTarget | null) =>
    target instanceof Element && Boolean(target.closest(`.${styles.media}`));

  const onPointerDown = (event: PointerEvent<HTMLLIElement>) => {
    touched.current = event.pointerType === 'touch';
    if (!touched.current || !isOnMedia(event.target)) return;
    held.current = false;
    activePointer.current = event.pointerId;
    start.current = { x: event.clientX, y: event.clientY };
    clearTimer();
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      held.current = true;
      setHolding(true);
    }, HOLD_MS);
  };

  const onPointerMove = (event: PointerEvent<HTMLLIElement>) => {
    if (event.pointerId !== activePointer.current || !start.current) return;
    if (holdTimer.current == null) return;
    const dx = event.clientX - start.current.x;
    const dy = event.clientY - start.current.y;
    if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) endHold();
  };

  // A touch tap toggles the overlay; a hold only peeks and its release click is ignored.
  const onClick = (event: MouseEvent<HTMLLIElement>) => {
    if (!touched.current || !isOnMedia(event.target)) return;
    if (held.current) {
      held.current = false;
      return;
    }
    setDismissed(false);
    setLatched((open) => !open);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key !== 'Escape') return;
    setDismissed(true);
    setLatched(false);
  };

  return (
    <li
      ref={cardRef}
      className={styles.card}
      data-open={holding || latched ? '' : undefined}
      data-overlay-dismissed={dismissed ? '' : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endHold}
      onPointerCancel={endHold}
      onPointerLeave={endHold}
      onClick={onClick}
      onContextMenu={(event) => {
        if (isOnMedia(event.target)) event.preventDefault();
      }}
      onMouseLeave={() => setDismissed(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setDismissed(false);
        }
      }}
      onKeyDown={onKeyDown}
    >
      {children}
    </li>
  );
}
